import type { FC, MouseEvent, ReactNode } from 'react'
import type { AdvertMediaId } from '@/types'

import { useCallback, useEffect, useRef, useState } from 'react'

import {
    Maximize2,
    Minimize2,
    Pause,
    Play,
    RotateCcw
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { LogoMark } from '@/components/brand/logo'
import { SCENE_COMPONENTS } from '@/components/advert/advert-scenes'

import {
    ADVERT_DURATION,
    ADVERT_MEDIA,
    ADVERT_SCENES,
    ADVERT_STARTS
} from '@/constants/advert'

type PlayerStatus = 'idle' | 'playing' | 'paused' | 'ended'

interface MediaLayer {
    id: AdvertMediaId
    position: string
    shade: string
}

const MEDIA_LAYERS: MediaLayer[] = [
    {
        id: 'local',
        position: 'object-[68%_60%] sm:object-center',
        shade: 'bg-linear-to-r from-ink/70 via-ink/20 to-transparent'
    },
    {
        id: 'big',
        position: 'object-[58%_50%] sm:object-center',
        shade: 'bg-linear-to-l from-ink/70 via-ink/20 to-transparent'
    }
]

const LAST_SCENE = ADVERT_SCENES.length - 1
const TOTAL_SECONDS = Math.round(ADVERT_DURATION / 1000)

const formatTime = (seconds: number): string => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

const sceneIndexAt = (elapsed: number): number => {
    for (let index = LAST_SCENE; index >= 0; index--) {
        if (elapsed >= ADVERT_STARTS[index]) return index
    }

    return 0
}

const AdvertPlayer: FC = (): ReactNode => {
    const playerRef = useRef<HTMLDivElement>(null)
    const fillRefs = useRef<Array<HTMLSpanElement | null>>([])
    const videoRefs = useRef<Partial<Record<AdvertMediaId, HTMLVideoElement | null>>>({})

    const statusRef = useRef<PlayerStatus>('idle')
    const elapsedRef = useRef(0)
    const sceneRef = useRef(0)
    const secondRef = useRef(0)
    const autoPausedRef = useRef(false)
    const startedRef = useRef(false)
    const inViewRef = useRef(false)

    const [status, setStatusState] = useState<PlayerStatus>('idle')
    const [scene, setScene] = useState(0)
    const [runId, setRunId] = useState(0)
    const [second, setSecond] = useState(0)
    const [canFullscreen, setCanFullscreen] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)

    const setStatus = useCallback((next: PlayerStatus) => {
        statusRef.current = next
        setStatusState(next)
    }, [])

    // The progress bar moves every frame, so it is painted directly instead of through state.
    const paint = useCallback((elapsed: number) => {
        ADVERT_SCENES.forEach((item, index) => {
            const node = fillRefs.current[index]

            if (!node) return

            const progress = Math.min(Math.max((elapsed - ADVERT_STARTS[index]) / item.duration, 0), 1)

            node.style.transform = `scaleX(${progress})`
        })
    }, [])

    const syncSecond = useCallback((elapsed: number) => {
        const next = Math.floor(elapsed / 1000)

        if (next === secondRef.current) return

        secondRef.current = next
        setSecond(next)
    }, [])

    const seek = useCallback((index: number) => {
        const start = ADVERT_STARTS[index]

        elapsedRef.current = start
        sceneRef.current = index
        setScene(index)
        setRunId(value => value + 1)
        syncSecond(start)
        paint(start)
    }, [paint, syncSecond])

    const play = useCallback(() => {
        autoPausedRef.current = false
        startedRef.current = true

        if (statusRef.current === 'ended') seek(0)

        setStatus('playing')
    }, [seek, setStatus])

    const pause = useCallback((auto = false) => {
        if (statusRef.current !== 'playing') return

        autoPausedRef.current = auto
        setStatus('paused')
    }, [setStatus])

    const replay = useCallback(() => {
        autoPausedRef.current = false
        startedRef.current = true
        seek(0)
        setStatus('playing')
    }, [seek, setStatus])

    const jumpTo = useCallback((index: number) => {
        autoPausedRef.current = false
        startedRef.current = true
        seek(index)
        setStatus('playing')
    }, [seek, setStatus])

    const togglePlay = useCallback(() => {
        if (statusRef.current === 'playing') pause()
        else play()
    }, [pause, play])

    const toggleFullscreen = useCallback(async () => {
        try {
            if (document.fullscreenElement) await document.exitFullscreen()
            else await playerRef.current?.requestFullscreen()
        } catch {
            // The browser refused full screen, so the advert simply stays inline.
        }
    }, [])

    const handleQuote = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
        if (!document.fullscreenElement) return

        event.preventDefault()

        document.exitFullscreen()
            .catch(() => undefined)
            .finally(() => document.getElementById('contact')?.scrollIntoView({ block: 'start' }))
    }, [])

    // The clock: advances while playing, switches scenes and stops at the end.
    useEffect(() => {
        if (status !== 'playing') return

        let frame = 0
        let last = performance.now()

        const tick = (now: number) => {
            const delta = Math.max(0, Math.min(now - last, 64))

            last = now

            const elapsed = Math.min(elapsedRef.current + delta, ADVERT_DURATION)

            elapsedRef.current = elapsed
            paint(elapsed)
            syncSecond(elapsed)

            const index = sceneIndexAt(elapsed)

            if (index !== sceneRef.current) {
                sceneRef.current = index
                setScene(index)
            }

            if (elapsed >= ADVERT_DURATION) {
                setStatus('ended')
                return
            }

            frame = requestAnimationFrame(tick)
        }

        frame = requestAnimationFrame(tick)

        return () => cancelAnimationFrame(frame)
    }, [status, paint, setStatus, syncSecond])

    const activeMedia = ADVERT_SCENES[scene].media

    // Each clip starts from its first frame whenever its scene begins.
    useEffect(() => {
        if (!activeMedia) return

        const video = videoRefs.current[activeMedia]

        if (video) video.currentTime = 0
    }, [activeMedia, scene, runId])

    useEffect(() => {
        MEDIA_LAYERS.forEach(layer => {
            const video = videoRefs.current[layer.id]

            if (!video) return

            if (layer.id === activeMedia && status === 'playing') {
                const attempt = video.play()

                if (attempt) attempt.catch(() => undefined)
            } else {
                video.pause()
            }
        })
    }, [activeMedia, status, runId])

    // Starts the advert when it scrolls into view, and pauses it while it is
    // off screen or the tab is hidden. Visitors who prefer reduced motion press play themselves.
    useEffect(() => {
        const node = playerRef.current

        if (!node) return

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        const observer = new IntersectionObserver(([entry]) => {
            const visible = entry.isIntersecting && entry.intersectionRatio >= 0.35

            inViewRef.current = visible

            if (visible) {
                if (statusRef.current === 'idle' && !startedRef.current && !reduceMotion && !document.hidden) play()
                else if (statusRef.current === 'paused' && autoPausedRef.current) play()
            } else if (!entry.isIntersecting) {
                pause(true)
            }
        }, { threshold: [0, 0.35] })

        const handleVisibility = () => {
            if (document.hidden) pause(true)
            else if (statusRef.current === 'paused' && autoPausedRef.current && inViewRef.current) play()
        }

        observer.observe(node)
        document.addEventListener('visibilitychange', handleVisibility)

        return () => {
            observer.disconnect()
            document.removeEventListener('visibilitychange', handleVisibility)
        }
    }, [pause, play])

    useEffect(() => {
        setCanFullscreen(Boolean(document.fullscreenEnabled))

        const handleChange = () => setIsFullscreen(document.fullscreenElement === playerRef.current)

        document.addEventListener('fullscreenchange', handleChange)

        return () => document.removeEventListener('fullscreenchange', handleChange)
    }, [])

    const current = ADVERT_SCENES[scene]
    const SceneComponent = SCENE_COMPONENTS[current.id]
    const showBadge = status !== 'idle' && Boolean(current.badge)
    const playLabel = status === 'playing' ? 'Pause the advert' : status === 'ended' ? 'Replay the advert' : 'Play the advert'

    return (
        <div
            ref={playerRef}
            className={cn('w-full', isFullscreen && 'flex h-full flex-col items-center justify-center bg-black p-3 sm:p-6')}
        >
            <div className={cn('w-full', isFullscreen && 'w-[min(100%,calc((100dvh_-_6rem)*0.8))] sm:w-[min(100%,calc((100dvh_-_6rem)*1.7778))]')}>
                <div
                    role='region'
                    aria-roledescription='video'
                    aria-label='Blitz Tech Services advert'
                    data-state={status}
                    className='ad-stage @container relative aspect-[4/5] w-full overflow-hidden rounded-t-[1.25rem] bg-ink sm:aspect-video'
                >
                    <div aria-hidden='true' className='ad-grid absolute inset-0' />

                    {MEDIA_LAYERS.map(layer => (
                        <div
                            key={layer.id}
                            aria-hidden='true'
                            className={cn('absolute inset-0 transition-opacity duration-700', activeMedia === layer.id ? 'opacity-100' : 'opacity-0')}
                        >
                            <video
                                ref={node => {
                                    // Set muted on the element itself too: browsers only let silent videos start on their own.
                                    if (node) {
                                        node.muted = true
                                        node.defaultMuted = true
                                    }

                                    videoRefs.current[layer.id] = node
                                }}
                                src={ADVERT_MEDIA[layer.id].video}
                                poster={ADVERT_MEDIA[layer.id].poster}
                                muted
                                loop
                                playsInline
                                preload='auto'
                                className={cn('size-full object-cover', layer.position)}
                            />
                            <div className='absolute inset-0 bg-linear-to-t from-ink/95 via-ink/35 to-ink/5' />
                            <div className={cn('absolute inset-0', layer.shade)} />
                        </div>
                    ))}

                    <div aria-hidden='true' className='ad-vignette pointer-events-none absolute inset-0' />

                    <div
                        aria-hidden='true'
                        className={cn(
                            'absolute left-[4cqw] top-[4cqw] z-10 flex items-center gap-[1.6cqw] transition-opacity duration-500 sm:left-[2.6cqw] sm:top-[2.6cqw] sm:gap-[0.9cqw]',
                            showBadge ? 'opacity-100' : 'opacity-0'
                        )}
                    >
                        <LogoMark className='size-[7cqw] sm:size-[3.6cqw]' />
                        <span className='font-heading text-[length:max(9px,2.4cqw)] font-bold uppercase tracking-[0.14em] text-paper/90 sm:text-[length:max(9px,1.15cqw)]'>
                            Blitz Tech Services
                        </span>
                    </div>

                    <SceneComponent
                        key={`${scene}-${runId}`}
                        duration={current.duration}
                        onQuote={handleQuote}
                        onReplay={replay}
                    />

                    <div
                        inert={status !== 'idle'}
                        className={cn(
                            'absolute inset-0 z-20 flex flex-col items-center justify-center bg-ink px-[6cqw] text-center transition-opacity duration-300',
                            status === 'idle' ? 'opacity-100' : 'pointer-events-none opacity-0'
                        )}
                    >
                        <div aria-hidden='true' className='ad-grid absolute inset-0' />
                        <div
                            aria-hidden='true'
                            className='absolute left-1/2 top-1/2 size-[80cqw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-volt/10 blur-[10cqw] sm:size-[56cqw]'
                        />

                        <LogoMark className='relative size-[18cqw] sm:size-[9cqw]' />

                        <p className='relative mt-[4cqw] font-heading text-[length:6cqw] font-extrabold tracking-[-0.02em] text-paper sm:mt-[2.4cqw] sm:text-[length:3.4cqw]'>
                            Blitz Tech Services
                        </p>
                        <p className='relative mt-[1.6cqw] text-[length:max(12px,3.4cqw)] text-paper/65 sm:mt-[1cqw] sm:text-[length:max(12px,1.5cqw)]'>
                            The advert · {formatTime(TOTAL_SECONDS)}
                        </p>

                        <button
                            type='button'
                            onClick={play}
                            className='relative mt-[6cqw] inline-flex items-center gap-2.5 rounded-full bg-volt px-6 py-3 text-sm font-bold text-ink shadow-lg shadow-volt/20 transition-transform hover:scale-[1.03] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paper sm:mt-[3cqw] sm:text-base'
                        >
                            <Play className='size-5 fill-current' aria-hidden='true' />
                            Play the advert
                        </button>
                    </div>
                </div>

                <div className='flex items-center gap-2 rounded-b-[1.25rem] border border-t-0 border-paper/10 bg-ink-soft px-2.5 py-2 sm:gap-3 sm:px-4'>
                    <button
                        type='button'
                        onClick={togglePlay}
                        aria-label={playLabel}
                        className='flex size-10 shrink-0 items-center justify-center rounded-full bg-volt text-ink transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-volt'
                    >
                        {status === 'playing' && <Pause className='size-4 fill-current' aria-hidden='true' />}
                        {status === 'ended' && <RotateCcw className='size-4' aria-hidden='true' />}
                        {(status === 'idle' || status === 'paused') && <Play className='size-4 translate-x-px fill-current' aria-hidden='true' />}
                    </button>

                    <div className='flex min-w-0 flex-1 items-center gap-1'>
                        {ADVERT_SCENES.map((item, index) => (
                            <button
                                key={item.id}
                                type='button'
                                onClick={() => jumpTo(index)}
                                aria-label={`Play scene ${index + 1}: ${item.label}`}
                                title={item.label}
                                className='group flex h-8 min-w-0 items-center rounded focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-volt'
                                style={{ flexGrow: item.duration, flexBasis: 0 }}
                            >
                                <span className='block h-1 w-full overflow-hidden rounded-full bg-paper/15 transition-[height] duration-150 group-hover:h-1.5'>
                                    <span
                                        ref={node => {
                                            fillRefs.current[index] = node
                                        }}
                                        className='block h-full w-full origin-left rounded-full bg-volt'
                                        style={{ transform: 'scaleX(0)' }}
                                    />
                                </span>
                            </button>
                        ))}
                    </div>

                    <span className='hidden shrink-0 font-mono text-xs tabular-nums text-paper/60 sm:block'>
                        {formatTime(second)} / {formatTime(TOTAL_SECONDS)}
                    </span>

                    {canFullscreen && (
                        <button
                            type='button'
                            onClick={toggleFullscreen}
                            aria-label={isFullscreen ? 'Exit full screen' : 'Watch full screen'}
                            className='flex size-10 shrink-0 items-center justify-center rounded-full text-paper/75 transition-colors hover:bg-paper/10 hover:text-paper focus-visible:outline-2 focus-visible:outline-volt'
                        >
                            {isFullscreen ? <Minimize2 className='size-4' aria-hidden='true' /> : <Maximize2 className='size-4' aria-hidden='true' />}
                        </button>
                    )}
                </div>
            </div>

            <div className='sr-only'>
                <p>
                    Advert script
                </p>
                <ol>
                    {ADVERT_SCENES.map(item => (
                        <li key={item.id}>
                            {item.transcript}
                        </li>
                    ))}
                </ol>
            </div>
        </div>
    )
}

export default AdvertPlayer
